import { useEffect } from 'react';

const SEO = ({ 
  title, 
  description, 
  keywords, 
  image, 
  url, 
  type = 'website',
  structuredData 
}) => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://manga-versus.com';
  const defaultImage = `${baseUrl}/og-image.jpg`;
  
  const defaultTitle = "Manga Versus - Anime Powerscaling & Character Voting Platform";
  const defaultDescription = "Vote on anime character battles and power scaling debates. Join the ultimate manga character rankings with real-time community voting. Compare power levels and popularity!";
  const defaultKeywords = "anime powerscaling, manga character voting, anime vs battles, character tier list, anime character rankings, power scaling debate, anime strength comparison";

  const fullTitle = title ? `${title} | Manga Versus` : defaultTitle;
  const fullUrl = url ? `${baseUrl}${url}` : baseUrl;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update or create meta tags
    const updateMeta = (name, content, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector);
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Update canonical link
    const updateCanonical = (href) => {
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', href);
    };

    // Update structured data
    const updateStructuredData = (data) => {
      let script = document.querySelector('script[type="application/ld+json"]:not([data-permanent])');
      if (script) {
        script.remove();
      }
      if (data) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        script.textContent = JSON.stringify(data);
        document.head.appendChild(script);
      }
    };

    // Apply all meta tags
    updateMeta('description', description || defaultDescription);
    updateMeta('keywords', keywords || defaultKeywords);
    updateMeta('author', 'Manga Versus');
    updateMeta('robots', 'index, follow');
    updateMeta('language', 'English');

    // Open Graph tags
    updateMeta('og:type', type, true);
    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', description || defaultDescription, true);
    updateMeta('og:image', image || defaultImage, true);
    updateMeta('og:url', fullUrl, true);
    updateMeta('og:site_name', 'Manga Versus', true);

    // Twitter tags
    updateMeta('twitter:card', 'summary_large_image', true);
    updateMeta('twitter:title', fullTitle, true);
    updateMeta('twitter:description', description || defaultDescription, true);
    updateMeta('twitter:image', image || defaultImage, true);

    // Update canonical and structured data
    updateCanonical(fullUrl);
    updateStructuredData(structuredData);

    // Cleanup function
    return () => {
      // Reset to default title when component unmounts
      document.title = defaultTitle;
    };
  }, [title, description, keywords, image, url, type, structuredData, fullTitle, fullUrl, defaultTitle, defaultDescription, defaultKeywords, defaultImage]);

  return null; // This component doesn't render anything
};

export default SEO;